import { fetchWithAuth } from '../utils/fetchWithAuth';

/* ========= helpers ========= */
const parseJSONSafe = async (res) => {
  const text = await res.text();
  if (!text) return {};
  try { return JSON.parse(text); } catch { return {}; }
};
const okOrNullIf404 = async (res) => {
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return parseJSONSafe(res);
};
const tryGet = async (urls) => { for (const u of urls) { const r = await okOrNullIf404(await fetchWithAuth(u, { method: 'GET' })); if (r !== null) return r; } throw new Error('Endpoint no encontrado'); };
const tryPost = async (urls, init) => { for (const u of urls) { const r = await okOrNullIf404(await fetchWithAuth(u, { ...init, method: 'POST' })); if (r !== null) return r; } throw new Error('Endpoint no encontrado'); };
const tryPut  = async (urls, init) => { for (const u of urls) { const r = await okOrNullIf404(await fetchWithAuth(u, { ...init, method: 'PUT' }));  if (r !== null) return r; } throw new Error('Endpoint no encontrado'); };
const tryDelete = async (urls) => { for (const u of urls) { const r = await okOrNullIf404(await fetchWithAuth(u, { method: 'DELETE' })); if (r !== null) return r; } throw new Error('Endpoint no encontrado'); };
const compact = (obj) => Object.fromEntries(Object.entries(obj).filter(([,v]) => v !== undefined && v !== null && !(Array.isArray(v)&&v.length===0) && String(v) !== ''));
const qs = (params) => { if (!params) return ''; const s = new URLSearchParams(compact(params)).toString(); return s ? `?${s}` : ''; };

const normalizeMinute = (m = {}) => {
  const meetingId = m.meeting?.id ?? m.meetingId ?? m.meeting_id ?? null;
  const viewUrl = m.viewUrl ?? m.fileSecureUrl ?? m.secureUrl ?? m.url ?? m.fileUrl ?? m.path ?? null;
  const downloadUrl = m.downloadUrl ?? m.fileSecureUrl ?? m.secureUrl ?? m.url ?? m.fileUrl ?? m.path ?? null;
  const title = m.title ?? m.name ?? m.filename ?? m.originalName ?? null;
  const date = m.date ?? m.created_at ?? m.createdAt ?? null;
  return { ...m, meetingId, viewUrl, downloadUrl, title, date };
};
const unwrap = (x) => x?.data?.data ?? x?.data ?? x?.item ?? x?.minute ?? x;

/* ===== Summary ===== */
export const crmListSummary = async (params) => {
  try {
    const r = await fetchWithAuth(`/vinculation-crm/summary${qs(params)}`, { method: 'GET' });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const data = await parseJSONSafe(r);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    return [];
  } catch {
    const r2 = await fetchWithAuth('/companies', { method: 'GET' });
    if (!r2.ok) throw new Error(`HTTP ${r2.status}`);
    return await parseJSONSafe(r2);
  }
};

/* ===== Detail ===== */
export const crmCompanyDetail = async (rutOrName, params) => {
  try {
    const q = qs(params);
    const out = await tryGet([
      `/vinculation-crm/company/${encodeURIComponent(rutOrName)}${q}`,
      `/crm/company/${encodeURIComponent(rutOrName)}${q}`,
      `/membership-crm/company/${encodeURIComponent(rutOrName)}${q}`,
    ]);
    const minutesRaw = Array.isArray(out?.minutes) ? out.minutes : [];
    return {
      company: out?.company ?? null,
      meetings: Array.isArray(out?.meetings) ? out.meetings : [],
      minutes: minutesRaw.map(normalizeMinute),
      notes: Array.isArray(out?.notes) ? out.notes : [],
      counters: out?.counters,
    };
  } catch {
    return { company: null, meetings: [], minutes: [], notes: [] };
  }
};

/* ===== Meetings ===== */
export const crmCreateMeeting = async ({ rut, date, note, title, description, category, location, participants }) => {
  const body = JSON.stringify(compact({ date, note, title, description, category, location, participants }));
  return await tryPost(
    [
      `/vinculation-crm/company/${encodeURIComponent(rut)}/meetings`,
      `/crm/company/${encodeURIComponent(rut)}/meetings`,
      `/membership-crm/company/${encodeURIComponent(rut)}/meetings`,
    ],
    { headers: { 'Content-Type': 'application/json' }, body }
  );
};

export const crmUpdateMeeting = async (id, payload) => {
  const body = JSON.stringify(compact(payload));
  return await tryPut(
    [
      `/vinculation-crm/meetings/${encodeURIComponent(id)}`,
      `/crm/meetings/${encodeURIComponent(id)}`,
      `/membership-crm/meetings/${encodeURIComponent(id)}`,
    ],
    { headers: { 'Content-Type': 'application/json' }, body }
  );
};

export const crmDeleteMeeting = async (id, rut) => {
  const urls = [
    `/vinculation-crm/meetings/${encodeURIComponent(id)}`,
    `/crm/meetings/${encodeURIComponent(id)}`,
    `/membership-crm/meetings/${encodeURIComponent(id)}`,
  ];
  if (rut) {
    urls.push(
      `/vinculation-crm/company/${encodeURIComponent(rut)}/meetings/${encodeURIComponent(id)}`,
      `/crm/company/${encodeURIComponent(rut)}/meetings/${encodeURIComponent(id)}`,
      `/membership-crm/company/${encodeURIComponent(rut)}/meetings/${encodeURIComponent(id)}`
    );
  }
  return await tryDelete(urls);
};

/* ===== Minutes (Cloudinary) ===== */
export const crmUploadMinute = async ({ rut, title, date, file, meetingId, participants, tags }) => {
  const fd = new FormData();
  if (title) fd.append('title', title);
  if (date)  fd.append('date', date);
  if (meetingId) fd.append('meetingId', String(meetingId));
  if (participants) fd.append('participants', JSON.stringify(participants));
  if (tags) fd.append('tags', JSON.stringify(tags));
  fd.append('file', file);

  const raw = await tryPost(
    [
      `/vinculation-crm/company/${encodeURIComponent(rut)}/minutes/upload`,
      `/crm/company/${encodeURIComponent(rut)}/minutes/upload`,
      `/membership-crm/company/${encodeURIComponent(rut)}/minutes/upload`,
    ],
    { body: fd } // NO setear Content-Type
  );

  const minute = normalizeMinute(unwrap(raw));
  return Object.keys(minute).length ? minute : normalizeMinute({ meetingId, title, date });
};

export const crmDeleteMinute = async (id, rut) => {
  const urls = [
    `/vinculation-crm/minutes/${encodeURIComponent(id)}`,
    `/crm/minutes/${encodeURIComponent(id)}`,
    `/membership-crm/minutes/${encodeURIComponent(id)}`,
  ];
  if (rut) {
    urls.push(
      `/vinculation-crm/company/${encodeURIComponent(rut)}/minutes/${encodeURIComponent(id)}`,
      `/crm/company/${encodeURIComponent(rut)}/minutes/${encodeURIComponent(id)}`,
      `/membership-crm/company/${encodeURIComponent(rut)}/minutes/${encodeURIComponent(id)}`
    );
  }
  return await tryDelete(urls);
};

/* ===== Recent Meetings ===== */
export const crmGetRecentMeetings = async (limit = 3) => {
  const urls = [
    `/vinculation-crm/recent-meetings?limit=${limit}`,
    `/crm/recent-meetings?limit=${limit}`,
    `/membership-crm/recent-meetings?limit=${limit}`,
  ];
  
  for (const url of urls) {
    try {
      const r = await fetchWithAuth(url, { method: 'GET' });
      if (!r.ok) continue; // Intentar siguiente URL
      const data = await parseJSONSafe(r);
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.data)) return data.data;
      if (Array.isArray(data?.meetings)) return data.meetings;
      if (Array.isArray(data?.items)) return data.items;
      return [];
    } catch (e) {
      console.warn(`Error al obtener reuniones recientes desde ${url}:`, e);
      continue; // Intentar siguiente URL
    }
  }
  
  console.error('No se pudo obtener reuniones recientes desde ningún endpoint');
  return [];
};

/* ===== Notes ===== */
export const crmListNotes = async (rut, params) => {
  const q = qs(params);
  return await tryGet([
    `/vinculation-crm/company/${encodeURIComponent(rut)}/notes${q}`,
    `/crm/company/${encodeURIComponent(rut)}/notes${q}`,
    `/membership-crm/company/${encodeURIComponent(rut)}/notes${q}`,
  ]);
};
export const crmCreateNote = async ({ rut, date, title, body }) => {
  const json = JSON.stringify(compact({ date, title, body }));
  return await tryPost(
    [
      `/vinculation-crm/company/${encodeURIComponent(rut)}/notes`,
      `/crm/company/${encodeURIComponent(rut)}/notes`,
      `/membership-crm/company/${encodeURIComponent(rut)}/notes`,
    ],
    { headers: { 'Content-Type': 'application/json' }, body: json }
  );
};
export const crmUpdateNote = async (id, payload) => {
  const json = JSON.stringify(compact(payload));
  return await tryPut(
    [
      `/vinculation-crm/notes/${encodeURIComponent(id)}`,
      `/crm/notes/${encodeURIComponent(id)}`,
      `/membership-crm/notes/${encodeURIComponent(id)}`,
    ],
    { headers: { 'Content-Type': 'application/json' }, body: json }
  );
};
export const crmDeleteNote = async (id, rut) => {
  const urls = [
    `/vinculation-crm/notes/${encodeURIComponent(id)}`,
    `/crm/notes/${encodeURIComponent(id)}`,
    `/membership-crm/notes/${encodeURIComponent(id)}`,
  ];
  if (rut) {
    urls.push(
      `/vinculation-crm/company/${encodeURIComponent(rut)}/notes/${encodeURIComponent(id)}`,
      `/crm/company/${encodeURIComponent(rut)}/notes/${encodeURIComponent(id)}`,
      `/membership-crm/company/${encodeURIComponent(rut)}/notes/${encodeURIComponent(id)}`
    );
  }
  return await tryDelete(urls);
};

/* ===== Analytics ===== */
export const crmAnalytics = async (params) => {
  const r = await fetchWithAuth(`/vinculation-crm/analytics${qs(params)}`, { method: 'GET' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return parseJSONSafe(r);
};
